import {
  RIGHT_PANEL_FLOAT_DEFAULT_HEIGHT,
  RIGHT_PANEL_FLOAT_DEFAULT_WIDTH,
  RIGHT_PANEL_FLOAT_MIN_HEIGHT,
  RIGHT_PANEL_FLOAT_MIN_WIDTH,
  RIGHT_PANEL_TAB_CATEGORY_SPEND,
  RIGHT_PANEL_TAB_FINANCIAL,
  RIGHT_PANEL_TAB_LABELS,
  RIGHT_PANEL_TAB_NUTRITION,
  RIGHT_PANEL_TAB_QUANTITY,
  RIGHT_PANEL_TAB_RESISTANCE,
  RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX,
} from 'constants/uiConstants';
import { Tooltip } from 'antd';
import PlanActualSegmented from 'components/common/PlanActualSegmented';
import { getRootFontSize, pxToRem, remToPx } from 'utils/cssUnits';
import React, { useState } from 'react';
import { Rnd } from 'react-rnd';
import {
  PiChartPie,
  PiCurrencyDollar,
  PiEye,
  PiEyeSlash,
  PiLeaf,
  PiPushPin,
  PiScales,
  PiShieldCheck,
} from 'react-icons/pi';
import { useDispatch, useSelector } from 'react-redux';
import {
  FloatingGeometry,
  RightPanelTabId,
  setRightPanelCategoryFilter,
  setRightPanelChartVisible,
} from '../../redux/slices/InsightsSlice';
import { RootState } from '../../redux/Store';
import RightPanelTabRouter from './RightPanelTabRouter';

const TAB_ICONS: Record<RightPanelTabId, React.ReactElement> = {
  [RIGHT_PANEL_TAB_NUTRITION]: <PiLeaf size={14} />,
  [RIGHT_PANEL_TAB_FINANCIAL]: <PiCurrencyDollar size={14} />,
  [RIGHT_PANEL_TAB_CATEGORY_SPEND]: <PiChartPie size={14} />,
  [RIGHT_PANEL_TAB_QUANTITY]: <PiScales size={14} />,
  [RIGHT_PANEL_TAB_RESISTANCE]: <PiShieldCheck size={14} />,
};

// ── Edge strip layout constants (must match RightEdgeLabels) ─────────────────
// w-6 = 1.5rem, pt-16 = 4rem, h-24 = 6rem per label
const EDGE_STRIP_WIDTH_REM = 1.5;
const EDGE_LABEL_TOP_OFFSET_REM = 4;
const EDGE_LABEL_HEIGHT_REM = 6;


// Content area is wrapped in p-4 (1rem each side).
const CONTENT_PADDING_REM = 2;

/**
 * Clamp a geometry (all values in rem) so the panel is:
 *   • At least MIN_WIDTH × MIN_HEIGHT
 *   • No wider/taller than the viewport minus a 1rem safety margin
 *   • Fully within the viewport bounds
 */
function clampGeometry(geo: FloatingGeometry): FloatingGeometry {
  const fs = getRootFontSize();
  const vwRem = window.innerWidth / fs;
  const vhRem = window.innerHeight / fs;
  const marginRem = 1;

  const width = Math.min(
    Math.max(geo.width, RIGHT_PANEL_FLOAT_MIN_WIDTH),
    vwRem - marginRem
  );
  const height = Math.min(
    Math.max(geo.height, RIGHT_PANEL_FLOAT_MIN_HEIGHT),
    vhRem - marginRem
  );
  const x = Math.max(0, Math.min(geo.x, vwRem - width));
  const y = Math.max(0, Math.min(geo.y, vhRem - height));

  return { x, y, width, height };
}

/**
 * Default position: flush against the left edge of the edge strip,
 * vertically aligned with the tab's label in the strip.
 * labelIndex is the tab's 0-based position in the visible edge strip.
 */
function buildDefaultGeometry(labelIndex: number): FloatingGeometry {
  const fs = getRootFontSize();
  const vwRem = window.innerWidth / fs;
  return clampGeometry({
    x: vwRem - RIGHT_PANEL_FLOAT_DEFAULT_WIDTH - EDGE_STRIP_WIDTH_REM,
    y: EDGE_LABEL_TOP_OFFSET_REM + labelIndex * EDGE_LABEL_HEIGHT_REM,
    width: RIGHT_PANEL_FLOAT_DEFAULT_WIDTH,
    height: RIGHT_PANEL_FLOAT_DEFAULT_HEIGHT,
  });
}

// ── Component ────────────────────────────────────────────────────────────────

interface RightFloatingPanelProps {
  tabId: RightPanelTabId;
  /**
   * 0-based index of this tab in the visible edge strip.
   * Used to compute the default open position when savedGeometry is null.
   */
  labelIndex: number;
  /** Last-saved geometry (rem). null = first time — use default. */
  savedGeometry: FloatingGeometry | null;
  /** Called on drag-stop or resize-stop with the new geometry (rem). */
  onGeometryChange: (geometry: FloatingGeometry) => void;
  /** When true this panel is the topmost floating panel — header gets success-light bg. */
  isTopmost: boolean;
  /** Called on mousedown anywhere inside the panel — brings it to front. */
  onBringToFront: () => void;
  onPin: () => void;
  onClose: () => void;
}

/**
 * Freely draggable and resizable floating panel built on react-rnd.
 *
 * Geometry is stored in rem and converted to/from px at runtime so the
 * panel scales correctly with the user's browser font size.
 *
 * On mount, saved geometry is clamped against the current viewport so a
 * panel saved on a large monitor never opens off-screen on a smaller one.
 *
 * The title bar carries the `rnd-drag-handle` class so only dragging by
 * the header moves the panel; content area drags are not captured.
 *
 * This component must be rendered inside a `fixed inset-0 pointer-events-none`
 * wrapper so Rnd's absolute positioning is relative to the viewport and the
 * transparent wrapper does not intercept clicks outside the panel.
 */
const RightFloatingPanel: React.FC<RightFloatingPanelProps> = ({
  tabId,
  labelIndex,
  savedGeometry,
  isTopmost,
  onGeometryChange,
  onBringToFront,
  onPin,
  onClose,
}) => {
  const dispatch = useDispatch();
  const chartVisible = useSelector(
    (state: RootState) =>
      state.insightsReducer.rightPanel.chartVisible[tabId]
  );
  const categoryFilter = useSelector(
    (state: RootState) => state.insightsReducer.rightPanel.categoryFilter
  );

  // Local geometry state (rem). Initialised once from saved or default.
  const [geo, setGeo] = useState<FloatingGeometry>(() =>
    clampGeometry(savedGeometry ?? buildDefaultGeometry(labelIndex))
  );

  const updateGeo = (next: FloatingGeometry) => {
    setGeo(next);
    onGeometryChange(next);
  };

  // The shared control only has an effect (a single-column fallback to pick
  // Plan or Actual for) when this panel's content area is too narrow to
  // show both columns side by side.
  const isWide =
    (geo.width - CONTENT_PADDING_REM) * getRootFontSize() >=
    RIGHT_PANEL_TWO_COLUMN_MIN_WIDTH_PX;

  return (
    <Rnd
      position={{ x: remToPx(geo.x), y: remToPx(geo.y) }}
      size={{ width: remToPx(geo.width), height: remToPx(geo.height) }}
      minWidth={remToPx(RIGHT_PANEL_FLOAT_MIN_WIDTH)}
      minHeight={remToPx(RIGHT_PANEL_FLOAT_MIN_HEIGHT)}
      dragHandleClassName="rnd-drag-handle"
      enableResizing
      className="pointer-events-auto"
      onMouseDown={onBringToFront}
      onDragStop={(_e, d) => {
        updateGeo({
          ...geo,
          x: pxToRem(d.x),
          y: pxToRem(d.y),
        });
      }}
      onResizeStop={(_e, _dir, ref, _delta, pos) => {
        updateGeo({
          x: pxToRem(pos.x),
          y: pxToRem(pos.y),
          width: pxToRem(ref.offsetWidth),
          height: pxToRem(ref.offsetHeight),
        });
      }}
      style={{ zIndex: 50 }}
    >
      {/* Restore pointer events for the panel itself */}
      <div
        className="flex h-full flex-col overflow-hidden rounded border bg-white shadow-xl pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — drag handle */}
        <div
          className={`rnd-drag-handle flex cursor-grab items-center justify-between border-b px-3 py-2 active:cursor-grabbing select-none ${
            isTopmost ? 'bg-success-light' : 'bg-gray-50'
          }`}
        >
          <div
            className="flex items-center gap-1.5"
            onDoubleClick={onPin}
          >
            {TAB_ICONS[tabId]}
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-dark">
              {RIGHT_PANEL_TAB_LABELS[tabId]}
              {tabId === RIGHT_PANEL_TAB_FINANCIAL && (
                <span className="ml-1 font-semibold normal-case tracking-normal text-secondary-dark">
                  (Plan data only)
                </span>
              )}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Tooltip
              title={
                chartVisible ? (
                  <>Hide chart<br /><span className="opacity-75">Collapse the chart for this panel</span></>
                ) : (
                  <>Show chart<br /><span className="opacity-75">Display the chart for this panel</span></>
                )
              }
            >
              <button
                type="button"
                aria-label={chartVisible ? 'Hide chart' : 'Show chart'}
                onClick={() =>
                  dispatch(
                    setRightPanelChartVisible({ tabId, visible: !chartVisible })
                  )
                }
                className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
              >
                {chartVisible ? (
                  <PiEyeSlash size={14} />
                ) : (
                  <PiEye size={14} />
                )}
              </button>
            </Tooltip>
            <Tooltip title={<>Pin panel<br /><span className="opacity-75">Dock this panel to the side bar</span></>}>
              <button
                type="button"
                aria-label="Pin panel"
                onClick={onPin}
                className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
              >
                <PiPushPin size={14} />
              </button>
            </Tooltip>
            <Tooltip title={<>Close<br /><span className="opacity-75">Hide this floating panel</span></>}>
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="inline-flex h-6 w-6 items-center justify-center rounded-[var(--radius-button)] text-secondary-dark hover:bg-gray-bright active:bg-gray-200"
              >
                <span className="text-xs">✕</span>
              </button>
            </Tooltip>
          </div>
        </div>

        {/* ── Shared Plan/Actual control (OP-2333) ──────────────────────── */}
        {!isWide && (
          <div className="flex items-center justify-between border-b bg-white px-3 py-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-muted">
              Category
            </span>
            <PlanActualSegmented
              value={categoryFilter}
              onChange={(cat) => dispatch(setRightPanelCategoryFilter(cat))}
            />
          </div>
        )}

        {/* Content slot */}
        <div className="flex-1 overflow-y-auto p-4">
          <RightPanelTabRouter tabId={tabId} />
        </div>
      </div>
    </Rnd>
  );
};

export default RightFloatingPanel;
